import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components as Entradas } from '../tipos/eventos-entradas';
import type { components, operations } from '../tipos/listas-rrpp';

/**
 * Operaciones del **portal del RRPP** (10.12) con su `operationId` y los tipos generados de `listas-rrpp.yaml` (y
 * `listEvents` de `eventos-entradas.yaml`). Con el ámbito «lo suyo» la API ya filtra: el RRPP solo recibe sus listas
 * (con `my_quota`), sus enlaces, sus invitados, sus ventas atribuidas, sus comisiones y sus liquidaciones **cerradas**
 * (G790). La campana (`listMyNotices`…) y `listDeliveries`/`resendDelivery` ya están en `operaciones.ts` y `ops/clientes.ts`.
 *
 * Si `ops/listas.ts` (10.9 a 10.11) declara las mismas operaciones con la misma forma, sobra una de las dos copias.
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type ListaDeInvitados = Esquema<'GuestList'>;
export type EnlaceRrpp = Esquema<'PromoterLink'>;
export type Invitado = Esquema<'Guest'>;
export type AltaInvitado = Esquema<'GuestCreate'>;
export type InvitadoApuntado = Esquema<'GuestCreated'>;
export type CambioInvitado = Esquema<'GuestUpdate'>;
export type EnlacePersonal = Esquema<'PersonalLink'>;
export type VentaAtribuida = Esquema<'PromoterAttribution'>;
export type Comision = Esquema<'Commission'>;
export type Liquidacion = Esquema<'Settlement'>;
export type EstadoPaseInvitado = Esquema<'GuestPassState'>;
export type EventoPanel = Entradas['schemas']['Event'];

type Lista<T> = { data: T[]; next_cursor: string | null };
type ConsultaInvitados = NonNullable<operations['listGuests']['parameters']['query']>;
type ConsultaEnlaces = NonNullable<operations['listPromoterLinks']['parameters']['query']>;
type ConsultaComisiones = NonNullable<operations['listCommissions']['parameters']['query']>;
type ConsultaVentas = NonNullable<operations['listPromoterAttributions']['parameters']['query']>;
export type PaginaVentas = operations['listPromoterAttributions']['responses'][200]['content']['application/json'];

const id = encodeURIComponent;
const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;

export function operacionesPortalRrpp(api: ClienteApi) {
    return {
        /** listPromoterLinks (`listas.enlaces.ver`): un enlace por evento y el fijo; sin Instagram o sin web, la dirección nula (G517, G675). */
        listPromoterLinks: (businessId: string, filtros: ConsultaEnlaces & Pagina = {}) =>
            api.pedir<Lista<EnlaceRrpp>>('GET', r(businessId, `/promoter-links${consulta({ ...filtros })}`)),
        /** createPromoterFixedLink (`listas.enlaces.gestionar`): uno vivo por RRPP (G52). */
        createPromoterFixedLink: (businessId: string, miembro: string, clave = claveIdempotencia()) =>
            api.pedir<EnlaceRrpp>('POST', r(businessId, '/promoter-links'), { cuerpo: { member_id: miembro }, idempotencia: clave }),
        /** deactivatePromoterLink. */
        deactivatePromoterLink: (businessId: string, enlaceId: string, clave = claveIdempotencia()) =>
            api.pedir<EnlaceRrpp>('POST', r(businessId, `/promoter-links/${id(enlaceId)}/deactivation`), { idempotencia: clave }),
        /** regeneratePromoterLink: el anterior deja de valer; también sobre uno desactivado (G788). */
        regeneratePromoterLink: (businessId: string, enlaceId: string, clave = claveIdempotencia()) =>
            api.pedir<EnlaceRrpp>('POST', r(businessId, `/promoter-links/${id(enlaceId)}/regeneration`), { idempotencia: clave }),

        /** listGuests (`listas.invitados.ver`): con «lo suyo», solo los suyos; `pass_state` (G788). */
        listGuests: (businessId: string, filtros: ConsultaInvitados & Pagina = {}) => api.pedir<Lista<Invitado>>('GET', r(businessId, `/guests${consulta({ ...filtros })}`)),
        /** addGuest: Instagram o email, uno obligatorio, nunca teléfono (G49); `409 instagram_not_connected` (G788). */
        addGuest: (businessId: string, alta: AltaInvitado, clave = claveIdempotencia()) =>
            api.pedir<InvitadoApuntado>('POST', r(businessId, '/guests'), { cuerpo: alta, idempotencia: clave }),
        /** getGuest, con su `ETag` para `updateGuest`. */
        getGuest: (businessId: string, invitadoId: string): Promise<ConVersion<Invitado>> => api.pedirConVersion<Invitado>('GET', r(businessId, `/guests/${id(invitadoId)}`)),
        /** updateGuest (If-Match): nombre, acompañantes y respuestas, con la lista abierta. */
        updateGuest: (businessId: string, invitadoId: string, cambio: CambioInvitado, version: string): Promise<ConVersion<Invitado>> =>
            api.pedirConVersion<Invitado>('PATCH', r(businessId, `/guests/${id(invitadoId)}`), { cuerpo: cambio, siCoincide: version }),
        /** withdrawGuest: con motivo, solo con la lista abierta. */
        withdrawGuest: (businessId: string, invitadoId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<Invitado>('POST', r(businessId, `/guests/${id(invitadoId)}/withdrawal`), { cuerpo: { reason: motivo }, idempotencia: clave }),
        /** getGuestPersonalLink: solo invitados de Instagram activos; sin Instagram conectado, `instagram_url` nulo (G517). */
        getGuestPersonalLink: (businessId: string, invitadoId: string) => api.pedir<EnlacePersonal>('GET', r(businessId, `/guests/${id(invitadoId)}/personal-link`)),

        /** listPromoterAttributions (`rrpp.comisiones.ver`): sus ventas con `reference` y `label` (G507, G788). */
        listPromoterAttributions: (businessId: string, filtros: ConsultaVentas & Pagina = {}) =>
            api.pedir<PaginaVentas>('GET', r(businessId, `/promoter-attributions${consulta({ ...filtros })}`)),
        /** listCommissions (`rrpp.comisiones.ver`). */
        listCommissions: (businessId: string, filtros: ConsultaComisiones & Pagina = {}) =>
            api.pedir<Lista<Comision>>('GET', r(businessId, `/commissions${consulta({ ...filtros })}`)),
        /** listSettlements: el portal solo pide las cerradas (G790); `totals` de todas las que cumplen los filtros (G834). */
        listSettlements: (businessId: string, filtros: { status?: Liquidacion['status']; promoter_id?: string } & Pagina = {}) =>
            api.pedir<Esquema<'SettlementPage'>>('GET', r(businessId, `/settlements${consulta({ ...filtros })}`)),
        /** getSettlement: `404` en borradores y descartadas para «lo suyo» (G790). */
        getSettlement: (businessId: string, liquidacionId: string) => api.pedir<Liquidacion>('GET', r(businessId, `/settlements/${id(liquidacionId)}`)),
    };
}

export type OperacionesPortalRrpp = ReturnType<typeof operacionesPortalRrpp>;
